import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  HStack,
  Image,
  Text,
  useToast,
  Flex,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  Link,
  InputGroup,
  InputRightElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { DeleteIcon } from "@chakra-ui/icons";

const List = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchOtt, setSearchOtt] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const [newVideo, setNewVideo] = useState({
    title: "",
    ott: "",
    thumbnail: "",
    download_link: "",
    video_ott_link: "",
    cast: "",
    director: "",
    genre: "",
    guide: "",
    introduction: "",
    nation: "",
    published_year: "",
    subtitle_link: "",
    type: "",
    url_submit: "",
    url_submit_link: "",
    comments: "",
  });
  const itemsPerPage = 10;
  const toast = useToast();
  const navigate = useNavigate();
  const [inputPage, setInputPage] = useState("1");
  const [videoToDelete, setVideoToDelete] = useState(null);
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();
  const cancelRef = React.useRef();

  const fetchVideos = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("shorts_licensed_videos")
        .select(
          "id, thumbnail, title, ott, created_date, download_link, video_ott_link, comments",
          { count: "exact" }
        );

      // 검색 조건 적용
      if (searchTitle) {
        query = query.ilike("title", `%${searchTitle}%`);
      }
      if (searchOtt) {
        query = query.ilike("ott", `%${searchOtt}%`);
      }

      // 페이지네이션 적용
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to).order("created_date", { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      setVideos(data);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / itemsPerPage));
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast({
        title: "비디오 목록을 불러오는데 실패했습니다.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [currentPage]);

  useEffect(() => {
    setInputPage(String(currentPage));
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchVideos();
  };

  const handleAdd = () => {
    setNewVideo({
      title: "",
      ott: "",
      thumbnail: "",
      download_link: "",
      video_ott_link: "",
      cast: "",
      director: "",
      genre: "",
      guide: "",
      introduction: "",
      nation: "",
      published_year: "",
      subtitle_link: "",
      type: "",
      url_submit: "",
      url_submit_link: "",
      comments: "",
    });
    onAddOpen();
  };

  const handleEdit = async (id) => {
    try {
      const { data, error } = await supabase
        .from("shorts_licensed_videos")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setSelectedVideo(data);
      onOpen();
    } catch (error) {
      console.error("Error fetching video details:", error);
      toast({
        title: "비디오 정보를 불러오는데 실패했습니다.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const { error } = await supabase
        .from("shorts_licensed_videos")
        .update(selectedVideo)
        .eq("id", selectedVideo.id);

      if (error) throw error;

      toast({
        title: "비디오 정보가 수정되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onClose();
      fetchVideos();
    } catch (error) {
      console.error("Error updating video:", error);
      toast({
        title: "비디오 정보 수정에 실패했습니다.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedVideo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNewVideoInputChange = (e) => {
    const { name, value } = e.target;
    setNewVideo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from("shorts_licensed_videos")
        .insert([newVideo]);

      if (error) throw error;

      toast({
        title: "비디오가 추가되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onAddClose();
      fetchVideos();
    } catch (error) {
      console.error("Error creating video:", error);
      toast({
        title: "비디오 추가에 실패했습니다.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setCurrentPage(num);
    else
      toast({
        title: "올바른 페이지 번호를 입력하세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
  };

  const handleThumbnailUpload = async (file, isEdit = false) => {
    try {
      if (!file) return;

      // 파일 확장자 검사
      const fileExt = file.name.split(".").pop();
      const allowedExts = ["jpg", "jpeg", "png", "gif"];
      if (!allowedExts.includes(fileExt.toLowerCase())) {
        toast({
          title: "지원하지 않는 파일 형식입니다.",
          description: "jpg, jpeg, png, gif 파일만 업로드 가능합니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // 파일 크기 검사 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "파일 크기가 너무 큽니다.",
          description: "5MB 이하의 파일만 업로드 가능합니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `shorts_thumbnails/${fileName}`;

      // Supabase Storage에 파일 업로드
      const { error: uploadError } = await supabase.storage
        .from("image")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 업로드된 파일의 public URL 가져오기
      const {
        data: { publicUrl },
      } = supabase.storage.from("image").getPublicUrl(filePath);

      if (isEdit) {
        setSelectedVideo((prev) => ({
          ...prev,
          thumbnail: publicUrl,
        }));
      } else {
        setNewVideo((prev) => ({
          ...prev,
          thumbnail: publicUrl,
        }));
      }

      toast({
        title: "썸네일이 업로드되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      toast({
        title: "썸네일 업로드 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("shorts_licensed_videos")
        .delete()
        .eq("id", videoToDelete.id);

      if (error) throw error;

      toast({
        title: "비디오가 삭제되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onDeleteAlertClose();
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "비디오 삭제에 실패했습니다.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        <HStack spacing={4} mb={4}>
          <Input
            placeholder="제목 검색"
            value={searchTitle}
            width="370px"
            onChange={(e) => setSearchTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Input
            placeholder="OTT 검색"
            value={searchOtt}
            width="200px"
            onChange={(e) => setSearchOtt(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button colorScheme="blue" onClick={handleSearch}>
            검색
          </Button>

          <Button colorScheme="blue" onClick={handleAdd} ml="auto">
            추가
          </Button>
        </HStack>

        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>썸네일</Th>
                  <Th>제목</Th>
                  <Th>OTT</Th>
                  <Th>등록일</Th>
                  <Th>다운로드</Th>
                  <Th>관리</Th>
                </Tr>
              </Thead>
              <Tbody>
                {videos.map((video) => (
                  <Tr key={video.id}>
                    <Td>
                      <Link
                        href={video.video_ott_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={video.thumbnail}
                          alt={video.title}
                          boxSize="100px"
                          objectFit="cover"
                          cursor="pointer"
                        />
                      </Link>
                    </Td>
                    <Td>{video.title}</Td>
                    <Td>{video.ott}</Td>
                    <Td>{new Date(video.created_date).toLocaleDateString()}</Td>
                    <Td>
                      {video.download_link && (
                        <Link
                          href={video.download_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" colorScheme="green">
                            다운로드
                          </Button>
                        </Link>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          colorScheme="blue"
                          size="sm"
                          onClick={() => handleEdit(video.id)}
                        >
                          수정
                        </Button>
                        <Button
                          colorScheme="red"
                          size="sm"
                          onClick={() => {
                            setVideoToDelete(video);
                            onDeleteAlertOpen();
                          }}
                        >
                          삭제
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="center" mt={4} gap={2} align="center">
              <Button
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                isDisabled={currentPage === 1}
              >
                이전
              </Button>
              <Input
                value={inputPage}
                onChange={handlePageInput}
                onBlur={handlePageGo}
                size="sm"
                width="50px"
                textAlign="center"
                fontSize="sm"
                placeholder="페이지"
              />
              <Button size="sm" onClick={handlePageGo}>
                이동
              </Button>
              <Text fontSize="sm" px={2}>
                / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                isDisabled={currentPage === totalPages}
              >
                다음
              </Button>
            </Flex>
          </>
        )}
      </Box>

      {/* 수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>비디오 정보 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedVideo && (
              <>
                <FormControl mb={4}>
                  <FormLabel>제목</FormLabel>
                  <Input
                    name="title"
                    value={selectedVideo.title}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>OTT</FormLabel>
                  <Input
                    name="ott"
                    value={selectedVideo.ott}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>썸네일</FormLabel>
                  <InputGroup>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleThumbnailUpload(e.target.files[0], true)
                      }
                      p={1}
                    />
                    <InputRightElement width="4.5rem">
                      {selectedVideo?.thumbnail && (
                        <Image
                          src={selectedVideo.thumbnail}
                          alt="썸네일 미리보기"
                          boxSize="40px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      )}
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>다운로드 링크</FormLabel>
                  <Input
                    name="download_link"
                    value={selectedVideo.download_link}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>OTT 링크</FormLabel>
                  <Input
                    name="video_ott_link"
                    value={selectedVideo.video_ott_link}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>캐스팅</FormLabel>
                  <Textarea
                    name="cast"
                    value={selectedVideo.cast}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>감독</FormLabel>
                  <Input
                    name="director"
                    value={selectedVideo.director}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>장르</FormLabel>
                  <Input
                    name="genre"
                    value={selectedVideo.genre}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>가이드</FormLabel>
                  <Textarea
                    rows={5}
                    name="guide"
                    value={selectedVideo.guide}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>소개</FormLabel>
                  <Textarea
                    rows={6}
                    name="introduction"
                    value={selectedVideo.introduction}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>국가</FormLabel>
                  <Input
                    name="nation"
                    value={selectedVideo.nation}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>발행년도</FormLabel>
                  <Input
                    name="published_year"
                    value={selectedVideo.published_year}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>자막 링크</FormLabel>
                  <Input
                    name="subtitle_link"
                    value={selectedVideo.subtitle_link}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>타입</FormLabel>
                  <Input
                    name="type"
                    value={selectedVideo.type}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>제출 URL</FormLabel>
                  <Input
                    name="url_submit"
                    value={selectedVideo.url_submit}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>제출 링크</FormLabel>
                  <Input
                    name="url_submit_link"
                    value={selectedVideo.url_submit_link}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>코멘트</FormLabel>
                  <Textarea
                    name="comments"
                    value={selectedVideo.comments}
                    onChange={handleInputChange}
                    rows={4}
                  />
                </FormControl>
                <Button colorScheme="blue" mr={3} onClick={handleUpdate}>
                  저장
                </Button>
                <Button onClick={onClose}>취소</Button>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 추가 모달 */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>새 비디오 추가</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel>제목</FormLabel>
              <Input
                name="title"
                value={newVideo.title}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>OTT</FormLabel>
              <Input
                name="ott"
                value={newVideo.ott}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>썸네일</FormLabel>
              <InputGroup>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleThumbnailUpload(e.target.files[0], false)
                  }
                  p={1}
                />
                <InputRightElement width="4.5rem">
                  {newVideo?.thumbnail && (
                    <Image
                      src={newVideo.thumbnail}
                      alt="썸네일 미리보기"
                      boxSize="40px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  )}
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>다운로드 링크</FormLabel>
              <Input
                name="download_link"
                value={newVideo.download_link}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>OTT 링크</FormLabel>
              <Input
                name="video_ott_link"
                value={newVideo.video_ott_link}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>캐스팅</FormLabel>
              <Textarea
                name="cast"
                value={newVideo.cast}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>감독</FormLabel>
              <Input
                name="director"
                value={newVideo.director}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>장르</FormLabel>
              <Input
                name="genre"
                value={newVideo.genre}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>가이드</FormLabel>
              <Textarea
                rows={5}
                name="guide"
                value={newVideo.guide}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>소개</FormLabel>
              <Textarea
                rows={6}
                name="introduction"
                value={newVideo.introduction}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>국가</FormLabel>
              <Input
                name="nation"
                value={newVideo.nation}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>발행년도</FormLabel>
              <Input
                name="published_year"
                value={newVideo.published_year}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>자막 링크</FormLabel>
              <Input
                name="subtitle_link"
                value={newVideo.subtitle_link}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>타입</FormLabel>
              <Input
                name="type"
                value={newVideo.type}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>제출 URL</FormLabel>
              <Input
                name="url_submit"
                value={newVideo.url_submit}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>제출 링크</FormLabel>
              <Input
                name="url_submit_link"
                value={newVideo.url_submit_link}
                onChange={handleNewVideoInputChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>코멘트</FormLabel>
              <Textarea
                name="comments"
                value={newVideo.comments}
                onChange={handleNewVideoInputChange}
                rows={4}
              />
            </FormControl>
            <Button colorScheme="blue" mr={3} onClick={handleCreate}>
              추가
            </Button>
            <Button onClick={onAddClose}>취소</Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              비디오 삭제
            </AlertDialogHeader>

            <AlertDialogBody>
              정말로 "{videoToDelete?.title}" 비디오를 삭제하시겠습니까? 이
              작업은 되돌릴 수 없습니다.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default List;
